import { apiClient } from "../../apiClient";
import { ApiResponse } from "../../../types/api";
import {
  State,
  StateApiResponse,
  CreateStateRequest,
  UpdateStateRequest,
  GetStatesParams,
  GetStatesResponse,
} from "./type";

class StateService {
  private transformState(apiState: StateApiResponse): State {
    return {
      ...apiState,
    };
  }

  async getStates(params: GetStatesParams = {}): Promise<GetStatesResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.skip !== undefined)
        queryParams.append("skip", params.skip.toString());
      if (params.limit !== undefined)
        queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.countryId) queryParams.append("countryId", params.countryId);

      const response = await apiClient.get<ApiResponse<StateApiResponse[]>>(
        `/states/?${queryParams.toString()}`,
      );

      const states = response.data.data.map((state) =>
        this.transformState(state),
      );

      return {
        states,
        total: states.length,
        skip: params.skip || 0,
        limit: params.limit || 25,
      };
    } catch (error) {
      console.error("Failed to fetch states:", error);
      throw error;
    }
  }

  async getStatesByCountry(countryId: string): Promise<State[]> {
    try {
      const response = await apiClient.get<ApiResponse<StateApiResponse[]>>(
        `/states/country/${countryId}`,
      );

      return response.data.data.map((state) => this.transformState(state));
    } catch (error) {
      console.error(`Failed to fetch states for country ${countryId}:`, error);
      throw error;
    }
  }

  async getState(id: string): Promise<State> {
    try {
      const response = await apiClient.get<ApiResponse<StateApiResponse>>(
        `/states/${id}`,
      );

      return this.transformState(response.data.data);
    } catch (error) {
      console.error(`Failed to fetch state ${id}:`, error);
      throw error;
    }
  }

  async createState(data: CreateStateRequest): Promise<State> {
    try {
      const response = await apiClient.post<ApiResponse<StateApiResponse>>(
        "/states/",
        data,
      );

      return this.transformState(response.data.data);
    } catch (error) {
      console.error("Failed to create state:", error);
      throw error;
    }
  }

  async updateState(stateId: string, data: UpdateStateRequest): Promise<State> {
    try {
      const response = await apiClient.put<ApiResponse<StateApiResponse>>(
        `/states/${stateId}`,
        data,
      );
      return this.transformState(response.data.data);
    } catch (error) {
      console.error(`Failed to update state ${stateId}:`, error);
      throw error;
    }
  }

  async deleteState(id: string): Promise<void> {
    try {
      await apiClient.delete(`/states/${id}`);
    } catch (error) {
      console.error(`Failed to delete state ${id}:`, error);
      throw error;
    }
  }
}

export const stateService = new StateService();

export type {
  GetStatesParams,
  GetStatesResponse,
  CreateStateRequest,
  UpdateStateRequest,
};
